'use client';

// Match-alert push opt-in, mounted globally (AuthClientRoot) so it covers
// EVERY page for logged-in users. Built 1 Aug 2026 while the WABA is
// restricted — browser push replaced WhatsApp as the match-alert channel, so
// the ask is deliberately loud:
//   • not subscribed yet  -> full-screen modal (dismiss lasts one session)
//   • permission denied   -> slim persistent bar on every page
//   • subscribed/unsupported/admin pages -> nothing
//
// Copy is Hindi — the audience is Bihar government-school teachers.

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, BellOff, X } from 'lucide-react';

const SESSION_DISMISS_KEY = 'push-prompt-dismissed-session';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

type PromptState = 'hidden' | 'ask' | 'blocked' | 'done';

export default function PushNotificationPrompt() {
  const { user } = useAuth();
  const pathname = usePathname();
  const [state, setState] = useState<PromptState>('hidden');
  const [busy, setBusy] = useState(false);

  // Nested AuthClientRoots (e.g. the school-maps layout wraps its own inside
  // the root one) would mount this twice and stack two modals — first mount
  // wins, later ones render nothing.
  const [isPrimary] = useState(() => {
    if (typeof window === 'undefined') return true;
    const w = window as unknown as { __pushPromptMounted?: boolean };
    if (w.__pushPromptMounted) return false;
    w.__pushPromptMounted = true;
    return true;
  });
  useEffect(() => {
    if (!isPrimary) return;
    return () => {
      (window as unknown as { __pushPromptMounted?: boolean }).__pushPromptMounted = false;
    };
  }, [isPrimary]);

  const evaluate = useCallback(async () => {
    if (
      typeof window === 'undefined' ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window) ||
      !('Notification' in window)
    ) {
      setState('hidden');
      return;
    }
    if (Notification.permission === 'denied') {
      setState(sessionStorage.getItem(SESSION_DISMISS_KEY) ? 'hidden' : 'blocked');
      return;
    }
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        setState('hidden');
        return;
      }
    } catch {
      setState('hidden');
      return;
    }
    // Once per session, dismissible. The no-dismiss experiment (1 Aug ~15:00
    // IST) trapped users who swiped away Chrome's permission prompt behind an
    // undismissable overlay mid-form: 108 form views / 31 field changes / ZERO
    // submits in an hour, 19 enable-clicks vs ~7 completed subscriptions.
    // Forced opt-in costs applications; the popup re-asks next session anyway.
    setState(sessionStorage.getItem(SESSION_DISMISS_KEY) ? 'hidden' : 'ask');
  }, []);

  useEffect(() => {
    // Logged-in users only, and never over the admin panel.
    if (!isPrimary || !user || pathname?.startsWith('/ghackk-admin')) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- visibility depends on login/route/permission state that only exists client-side; evaluate() is async so this cannot be a render-phase computation
      setState('hidden');
      return;
    }
    evaluate();
  }, [isPrimary, user, pathname, evaluate]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        setState('blocked');
        return;
      }
      if (permission !== 'granted') return; // dismissed the browser prompt — keep ours up
      const key = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
      if (!key) throw new Error('push key missing');
      // serviceWorker.ready never REJECTS — if registration failed on this
      // device it waits forever, freezing the button on "चालू हो रहा है…"
      // after the user already granted permission. Race it with a timeout and
      // fall back to a fresh register(); if that also fails, the catch hides
      // the popup instead of holding the page hostage.
      const reg =
        (await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
        ])) || (await navigator.serviceWorker.register('/sw.js'));
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
      });
      const res = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      if (!res.ok) throw new Error('subscribe failed');
      setState('done');
      setTimeout(() => setState('hidden'), 3000);
    } catch (e) {
      console.error('push subscribe failed:', e);
      setState('hidden');
    } finally {
      setBusy(false);
    }
  }, []);

  const dismiss = useCallback(() => {
    sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    setState('hidden');
  }, []);

  // After the user says they flipped the browser setting: if permission is now
  // granted, subscribe() completes silently (requestPermission resolves
  // 'granted' without a prompt); if still denied, the popup stays with a nudge.
  const recheckBlocked = async () => {
    if (Notification.permission !== 'denied') {
      await subscribe();
    }
  };

  if (state === 'hidden') return null;

  // Permission denied: the browser will not re-ask, so the popup explains the
  // manual path. Cross hides it for this session; it returns next visit.
  if (state === 'blocked') {
    return (
      <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
        <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center">
          <button
            onClick={dismiss}
            aria-label="Close"
            className="absolute top-3 right-3 p-1.5 rounded-full text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-5">
            <BellOff className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3">
            🔕 नोटिफिकेशन बंद हैं
          </h2>
          <p className="text-sm sm:text-base text-zinc-700 leading-relaxed mb-4">
            मैच मिलते ही सूचना पाने के लिए नोटिफिकेशन चालू करना ज़रूरी है, ताकि आपको बार-बार
            ऑनलाइन आकर चेक न करना पड़े।
          </p>
          <div className="text-left text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-xl p-4 mb-5 space-y-1.5">
            <p className="font-semibold text-zinc-900">चालू करने का तरीका:</p>
            <p>1. ऊपर address bar में 🔒 (ताला) पर टैप करें</p>
            <p>2. <b>Permissions</b> / <b>Notifications</b> चुनें</p>
            <p>3. <b>Allow</b> कर दें</p>
          </div>
          <button
            onClick={recheckBlocked}
            disabled={busy}
            className="w-full py-4 sm:py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-lg sm:text-base disabled:opacity-60"
          >
            {busy ? 'जाँच हो रही है…' : 'मैंने Allow कर दिया — दोबारा जाँचें'}
          </button>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-xl text-sm font-medium flex items-center gap-2">
        <Bell className="h-4 w-4" />
        नोटिफिकेशन चालू हो गया — मैच मिलते ही आपको सूचना मिलेगी। ✅
      </div>
    );
  }

  // Full-screen ask.
  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl p-6 sm:p-8 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center">
        <button
          onClick={dismiss}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-full text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
          <Bell className="h-8 w-8 text-emerald-600" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3">
          🔔 मैच मिलते ही तुरंत सूचना पाएं!
        </h2>
        <p className="text-sm sm:text-base text-zinc-700 leading-relaxed mb-6">
          कृपया नोटिफिकेशन चालू करें ताकि जैसे ही आपके लिए कोई मैच मिले, हम आपको तुरंत मैसेज भेज
          सकें। इससे आपको बार-बार ऑनलाइन आकर चेक करने की ज़रूरत नहीं पड़ेगी।
        </p>
        <div className="space-y-2">
          <button
            onClick={subscribe}
            disabled={busy}
            className="w-full py-4 sm:py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-lg sm:text-base disabled:opacity-60"
          >
            {busy ? 'चालू हो रहा है…' : 'नोटिफिकेशन चालू करें'}
          </button>
          <button onClick={dismiss} className="w-full py-2 text-sm font-medium text-zinc-500 hover:text-zinc-700">
            बाद में
          </button>
        </div>
      </div>
    </div>
  );
}
